import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Crear instancia de axios
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // IMPORTANTE: Enviar cookies automáticamente
});

// Flag para evitar refresh loops
let isRefreshing = false;
let failedQueue = [];

// Procesar cola de requests fallidos después del refresh
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Interceptor para agregar token JWT a las peticiones
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar respuestas y auto-refresh en 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Si el error NO es 401 o ya intentamos refrescar, rechazar
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Si el request fallido es el mismo /auth/refresh, hacer logout
    if (originalRequest.url.includes('/auth/refresh')) {
      isRefreshing = false;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Si ya estamos refrescando, encolar el request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        })
        .catch(err => {
          return Promise.reject(err);
        });
    }

    // Marcar que estamos refrescando y que este request ya fue reintentado
    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // Intentar refrescar el token (usa refresh token de httpOnly cookie)
      const response = await axios.post(
        `${API_URL}/auth/refresh`,
        {},
        { withCredentials: true }
      );

      const { token: newToken, user } = response.data;

      // Guardar nuevo access token
      localStorage.setItem('token', newToken);
      if (user) {
        localStorage.setItem('user', JSON.stringify(user));
      }

      // Actualizar header del request original
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      
      // Procesar cola de requests pendientes
      processQueue(null, newToken);
      
      isRefreshing = false;

      // Reintentar request original con nuevo token
      return api(originalRequest);
    } catch (refreshError) {
      // Refresh falló - hacer logout
      processQueue(refreshError, null);
      isRefreshing = false;
      
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
      
      return Promise.reject(refreshError);
    }
  }
);

export default api;
